import { getActiveAct, getDarkness, band, smoothstep } from "./scrollMath";

// Registry of overlay DOM elements keyed by name. Components register via callback ref.
export const overlayEls = {};

export function registerOverlay(key, el) {
  if (el) overlayEls[key] = el;
  else delete overlayEls[key];
}

function apply(key, op, y = 0) {
  const el = overlayEls[key];
  if (!el) return;
  el.style.opacity = String(op);
  // Round to whole pixels: these elements are on their own GPU layer, and a
  // fractional translate rasterises the text off the pixel grid, which reads
  // as blurry body copy.
  el.style.transform = `translate3d(0, ${Math.round(y)}px, 0)`;
  el.style.pointerEvents = op > 0.55 ? "auto" : "none";
  el.style.visibility = op > 0.001 ? "visible" : "hidden";
}

// Handwritten marginalia: fade + "write-on" via clip-path. If reveal is null,
// clip-path is left to a one-shot CSS animation (used for the hero, which is at p=0).
function writeMarg(key, op, reveal) {
  const el = overlayEls[key];
  if (!el) return;
  el.style.opacity = String(op * 0.9);
  if (reveal !== null) {
    const r = Math.max(0, Math.min(1, reveal));
    el.style.clipPath = `inset(0 ${(1 - r) * 100}% 0 0)`;
  }
  el.style.visibility = op > 0.001 ? "visible" : "hidden";
}

// Scroll map. Five acts across the page:
//   intro 0–0.14 · noise 0.14–0.47 · product 0.47–0.75 · promise 0.75–0.93 ·
//   calm 0.93–1
// Act 04 (promise) was added later, so everything from the noise act onward
// was re-timed to make room rather than squeezing it into the tail.
export function updateOverlays(p) {
  // Hero fades out early
  const heroOp = 1 - smoothstep(0.05, 0.12, p);
  apply("hero", heroOp, (1 - heroOp) * -14);

  // Handwritten marginalia (write-on)
  const heroM = 1 - smoothstep(0.05, 0.11, p);
  writeMarg("m-hero-l", heroM, null);
  writeMarg("m-hero-r", heroM, null);
  const noiseM = band(p, 0.15, 0.21, 0.4, 0.45);
  writeMarg("m-noise-l", noiseM, smoothstep(0.17, 0.25, p));
  writeMarg("m-noise-r", noiseM, smoothstep(0.2, 0.28, p));
  const prodM = band(p, 0.49, 0.54, 0.71, 0.755);
  writeMarg("m-prod-l", prodM, smoothstep(0.5, 0.58, p));
  writeMarg("m-prod-r", prodM, smoothstep(0.53, 0.61, p));
  // Promise act marginalia, held inside each beat's own window so a reaction
  // never lingers over the next statement.
  // The write-on has to FINISH well before the fade-out begins, or the word is
  // only ever fully written at the moment it disappears. Each reveal completes
  // inside the beat's plateau, not at its trailing edge.
  const promiseM = band(p, 0.765, 0.795, 0.835, 0.86);
  writeMarg("m-promise-l", promiseM, smoothstep(0.77, 0.8, p));
  writeMarg("m-promise-r", promiseM, smoothstep(0.78, 0.812, p));
  const priceM = band(p, 0.86, 0.885, 0.905, 0.93);
  writeMarg("m-price-l", priceM, smoothstep(0.863, 0.888, p));
  writeMarg("m-price-r", priceM, smoothstep(0.871, 0.896, p));

  const footM = smoothstep(0.93, 0.97, p);
  writeMarg("m-foot", footM, smoothstep(0.93, 0.98, p));

  // Noise statements (sequential, inside the black void)
  // Each beat's fade-in starts exactly where the previous one's fade-out ends.
  // Overlapping them stacks two centred blocks of text on top of each other
  // mid-scroll, which reads as a rendering glitch rather than a transition.
  const n1 = band(p, 0.16, 0.2, 0.26, 0.3);
  const n2 = band(p, 0.3, 0.335, 0.375, 0.405);
  const n3 = band(p, 0.405, 0.435, 0.46, 0.49);
  apply("noise-1", n1, (1 - n1) * 10);
  apply("noise-2", n2, (1 - n2) * 10);
  apply("noise-3", n3, (1 - n3) * 10);

  // Product title persists across product act
  const titleOp = band(p, 0.47, 0.51, 0.73, 0.76);
  apply("product-title", titleOp, (1 - titleOp) * 10);

  const f1 = band(p, 0.49, 0.525, 0.565, 0.595);
  const f2 = band(p, 0.595, 0.625, 0.66, 0.69);
  const f3 = band(p, 0.69, 0.715, 0.73, 0.75);
  apply("feature-1", f1, (1 - f1) * 12);
  apply("feature-2", f2, (1 - f2) * 12);
  apply("feature-3", f3, (1 - f3) * 12);

  // Promise act: the three "No …" lines, then what it costs
  const promiseTitleOp = band(p, 0.755, 0.785, 0.905, 0.93);
  apply("promise-title", promiseTitleOp, (1 - promiseTitleOp) * 10);
  const pr1 = band(p, 0.765, 0.795, 0.835, 0.86);
  const pr2 = band(p, 0.86, 0.885, 0.905, 0.93);
  apply("promise-1", pr1, (1 - pr1) * 12);
  apply("promise-2", pr2, (1 - pr2) * 12);

  // Footer fades in and stays
  const footerOp = smoothstep(0.93, 0.97, p);
  apply("footer", footerOp, (1 - footerOp) * 14);

  // Scene mode for color flipping (nav, scrollbar)
  const dark = getDarkness(p);
  const scene = dark > 0.5 ? "noise" : "cream";
  if (document.body.dataset.scene !== scene) document.body.dataset.scene = scene;

  // Progress rail ticks
  const act = getActiveAct(p);
  for (let i = 0; i < 5; i++) {
    const el = overlayEls[`rail-${i}`];
    if (!el) continue;
    const active = i === act;
    el.style.height = active ? "26px" : "10px";
    el.style.opacity = active ? "1" : "0.4";
    el.style.background = active
      ? scene === "noise"
        ? "var(--bb-red-soft)"
        : "var(--bb-red)"
      : scene === "noise"
      ? "rgba(245,240,232,0.5)"
      : "rgba(10,10,10,0.35)";
  }
}
